import { useUpdateTenantBrandingAttribution } from '@supply-mover-2-0/web-ui';
import { useEffect } from 'react';
import { usePortalSiteStore } from '../stores/portalSiteStore';

/**
 * Applies the storefront's resolved "Powered by X" attribution override onto
 * the TenantBrandingProvider context (PD-14113).
 *
 * The backend bootstrap (`GET /api/portal/site`) resolves the override with
 * precedence — `portal_site.settings.brandAttribution` → tenant
 * `branding.attribution` instance setting → null. When it returns a value we
 * overwrite the build-time brand default; when it's null we leave the default
 * in place (e.g. a Workd-default storefront keeps "Powered by [Workd logo]").
 *
 * Side-effect only (renders nothing) — mirrors BrandThemeApplier so it can sit
 * inside <TenantGate> and react when the bootstrap response lands.
 */
export function PortalAttributionHydrator(): null {
	const setAttribution = useUpdateTenantBrandingAttribution();
	const brandAttribution = usePortalSiteStore(
		(s) => s.site?.brandAttribution ?? null,
	);

	useEffect(() => {
		if (brandAttribution == null) {
			// No tenant/storefront override — keep the build-time brand default.
			return;
		}

		setAttribution({
			show: brandAttribution.show,
			// A null label means "use the brand-default name"; coalesce to
			// undefined so the setter keeps the existing (default) label.
			label: brandAttribution.label ?? undefined,
			// logoUrl / logoUrlDark / linkUrl are nullable in the resolved
			// context — pass null through so an override that clears the logo
			// (e.g. a text-only white-label) wins over the brand default.
			logoUrl: brandAttribution.logoUrl,
			logoUrlDark: brandAttribution.logoUrlDark,
			linkUrl: brandAttribution.linkUrl,
		});
	}, [brandAttribution, setAttribution]);

	return null;
}
